import { createContext, useContext, useEffect, useRef, useState } from "react";
import io from "socket.io-client";
import { API_URL } from "../config";
import API from "../api";
import { capture } from "../instrument";

const SOCKET_RETRY_BASE_DELAY = 2000;
const SOCKET_RETRY_MAX_DELAY = 300000;
const REALTIME_DOWN_GRACE_MS = 5000;

const SocketContext = createContext({ socket: null, status: "connecting", isRealtimeDown: false, reconnect: () => {} });

export const useSocket = () => useContext(SocketContext);

export const SocketProvider = ({ children }) => {
  const [socket, setSocket] = useState(null);
  const [status, setStatus] = useState("connecting");
  const [connectionEpoch, setConnectionEpoch] = useState(0);
  const [isRealtimeDown, setIsRealtimeDown] = useState(false);
  const retryTimeoutRef = useRef(null);
  const retryDelayRef = useRef(SOCKET_RETRY_BASE_DELAY);

  const isConnected = status === "connected";
  useEffect(() => {
    if (isConnected) {
      setIsRealtimeDown(false);
      return;
    }
    const timer = setTimeout(() => setIsRealtimeDown(true), REALTIME_DOWN_GRACE_MS);
    return () => clearTimeout(timer);
  }, [isConnected, connectionEpoch]);

  const restartConnection = () => {
    clearTimeout(retryTimeoutRef.current);
    retryTimeoutRef.current = null;
    retryDelayRef.current = SOCKET_RETRY_BASE_DELAY;
    setSocket((currentSocket) => {
      currentSocket?.close();
      return null;
    });
    setStatus("connecting");
    setConnectionEpoch((currentEpoch) => currentEpoch + 1);
  };

  useEffect(
    () =>
      API.onTokenChange(() => {
        setIsRealtimeDown(false);
        restartConnection();
      }),
    [],
  );

  useEffect(() => {
    let currentSocket = null;
    let isUnmounted = false;

    const connect = () => {
      if (isUnmounted) return;
      if (!API.getToken()) {
        setSocket(null);
        setStatus("disconnected");
        return;
      }

      currentSocket = io(API_URL, {
        withCredentials: true,
        extraHeaders: { Authorization: `JWT ${API.getToken()}` },
        reconnection: false,
      });

      currentSocket.on("connect", () => {
        setStatus("connected");
        retryDelayRef.current = SOCKET_RETRY_BASE_DELAY;
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      });

      const onDisconnect = (error) => {
        if (error) capture(error);
        setStatus("disconnected");

        if (retryTimeoutRef.current || isUnmounted) return;
        if (!API.getToken()) return;

        retryTimeoutRef.current = setTimeout(() => {
          retryTimeoutRef.current = null;
          setStatus("connecting");
          currentSocket?.close();
          retryDelayRef.current = Math.min(retryDelayRef.current * 2, SOCKET_RETRY_MAX_DELAY);
          connect();
        }, retryDelayRef.current);
      };

      currentSocket.on("disconnect", () => onDisconnect());
      currentSocket.on("connect_error", onDisconnect);

      setSocket(currentSocket);
    };

    connect();

    return () => {
      isUnmounted = true;
      clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = null;
      currentSocket?.close();
    };
  }, [connectionEpoch]);

  return <SocketContext.Provider value={{ socket, status, isRealtimeDown, reconnect: restartConnection }}>{children}</SocketContext.Provider>;
};
